import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useApp } from '../context/AppContext';
import { playNotificationSound } from '../utils/notificationSound';

const slideInRight = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  background: ${(p) => p.$bg || '#0ea5e9'};
  color: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: ${slideInRight} 0.3s ease-out;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
`;

const Title = styled.div`
  font-weight: 800;
  margin-bottom: 4px;
`;

const CloseBtn = styled.button`
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-weight: 700;
  flex-shrink: 0;
  &:hover { background: rgba(255, 255, 255, 0.35); }
`;

const ACTION_META = {
  assigned: { label: 'Канбан · новая задача', bg: '#0284c7' },
  started: { label: 'Канбан · в работе', bg: '#d97706' },
  done: { label: 'Канбан · на проверку', bg: '#ca8a04' },
  returned: { label: 'Канбан · вернули', bg: '#dc2626' },
  approved: { label: 'Канбан · утверждено', bg: '#16a34a' },
};

function isForMe(payload, myId) {
  if (!myId || !payload) return false;
  if (Number(payload.targetUserId) === myId) return true;
  if (Array.isArray(payload.targetUserIds) && payload.targetUserIds.map(Number).includes(myId)) return true;
  // запасной канал: событие относится ко мне как к исполнителю
  if (Number(payload.assigneeId) === myId && payload.action === 'returned') return true;
  if (Number(payload.assigneeId) === myId && payload.action === 'approved') return true;
  return false;
}

export default function KanbanNotifications() {
  const { state } = useApp();
  const myId = Number(state.user?.id || state.user?.userId || 0);
  const [items, setItems] = useState([]);
  const seen = useRef(new Set());
  const cleanupRef = useRef(() => {});

  useEffect(() => {
    if (!myId) return;

    const onEvent = (payload) => {
      if (!isForMe(payload, myId)) return;
      const key = `${payload.action}-${payload.taskId}-${payload.at || ''}-${payload.targetUserId || ''}`;
      if (seen.current.has(key)) return;
      seen.current.add(key);
      const id = `kanban-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setItems((prev) => [...prev, { id, ...payload }]);
      playNotificationSound();
      setTimeout(() => {
        setItems((prev) => prev.filter((x) => x.id !== id));
      }, 12000);
    };

    const socket = typeof window !== 'undefined' ? window.socket : null;
    if (socket) {
      socket.on('kanban_task_status', onEvent);
      cleanupRef.current = () => socket.off('kanban_task_status', onEvent);
      return () => cleanupRef.current();
    }

    const intervalId = setInterval(() => {
      const s = typeof window !== 'undefined' ? window.socket : null;
      if (s) {
        clearInterval(intervalId);
        s.on('kanban_task_status', onEvent);
        cleanupRef.current = () => s.off('kanban_task_status', onEvent);
      }
    }, 500);

    return () => {
      clearInterval(intervalId);
      cleanupRef.current();
    };
  }, [myId]);

  if (!items.length) return null;

  return (
    <Container>
      {items.map((n) => {
        const meta = ACTION_META[n.action] || { label: 'Канбан', bg: '#0ea5e9' };
        return (
          <Item key={n.id} $bg={meta.bg}>
            <Body>
              <Title>{meta.label}</Title>
              <div>{n.message || n.title}</div>
            </Body>
            <CloseBtn type="button" onClick={() => setItems((prev) => prev.filter((x) => x.id !== n.id))}>
              ×
            </CloseBtn>
          </Item>
        );
      })}
    </Container>
  );
}
